import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import SyncIcon from '@mui/icons-material/Sync';
import { MRT_RowSelectionState } from 'material-react-table';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  COMMIT_FIND_SYNC_MUTATION,
  GET_ALL_FILTER_TYPES_QUERY,
  GET_FIND_SYNC_QUERY,
  UPDATE_SITE_MUTATION,
} from '../../../gql';
import {
  Button,
  FieldLabel,
  FormField,
  SurfaceAutocomplete,
  SurfaceSelect,
  SurfaceTextField,
} from '../../../components/design-system';
import {
  SyncFieldKey,
  SyncFields,
  SyncOptions,
  SyncRow,
  SyncTable,
} from './SyncTable';

interface Props {
  siteId?: string;
}

const pageStyles = { gap: 2 };
const cardStyles = {
  p: { xs: 2, sm: 3 },
  border: '1px solid',
  borderColor: 'softBorder',
  backgroundImage: 'none',
};
const configGridStyles = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'minmax(260px, 2fr) minmax(220px, 1fr)',
  },
  gap: 2,
};
const actionsStyles = { alignItems: 'flex-end', flexWrap: 'wrap' };
const statsStyles = { alignItems: 'center', flexWrap: 'wrap' };
const tableHeaderStyles = {
  alignItems: { xs: 'stretch', sm: 'flex-end' },
  justifyContent: 'space-between',
  flexWrap: 'wrap',
};
const reviewHeadingStyles = { gap: 0.75, minWidth: 0 };
const targetStyles = { minWidth: 220 };
const labelRowStyles = { alignItems: 'center' };
const infoButtonStyles = { p: 0.25, ml: 0.5, color: 'text.secondary' };
const infoIconStyles = { fontSize: 14 };
const emptyTabs: string[] = [];

interface Notice {
  severity: 'success' | 'error';
  text: string;
}

export const SiteSync = React.memo(({ siteId }: Props) => {
  const intl = useIntl();
  const [sheetId, setSheetId] = useState('');
  const [configuredTabs, setConfiguredTabs] = useState<string[]>([]);
  const [targetTab, setTargetTab] = useState('');
  const [drafts, setDrafts] = useState<Record<string, SyncFields>>({});
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [notice, setNotice] = useState<Notice | null>(null);
  const configQuery = useQuery(GET_FIND_SYNC_QUERY, {
    variables: { siteId, preview: false },
    skip: !siteId,
    fetchPolicy: 'network-only',
  });
  const filterQuery = useQuery(GET_ALL_FILTER_TYPES_QUERY, {
    fetchPolicy: 'cache-first',
    skip: !siteId,
  });
  const [loadPreview, previewQuery] = useLazyQuery(GET_FIND_SYNC_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [saveSite, saveState] = useMutation(UPDATE_SITE_MUTATION);
  const [commit, commitState] = useMutation(COMMIT_FIND_SYNC_MUTATION);
  const config = configQuery.data?.getFindSync?.config;
  const sync = previewQuery.data?.getFindSync;
  const sheetUrl = sync?.sheetUrl || configQuery.data?.getFindSync?.sheetUrl;
  const rows = useMemo<SyncRow[]>(() => sync?.rows || [], [sync?.rows]);
  const tabs = useMemo<string[]>(() => sync?.tabs || [], [sync?.tabs]);
  const changeCount = useMemo(
    () => rows.filter((row) => !row.conflict).length,
    [rows]
  );
  const choices = useMemo<SyncOptions>(
    () => ({
      type: (filterQuery.data?.getAllFilterTypes?.findTypes || []).map(
        (type) => type.id
      ),
      material: filterQuery.data?.getAllFilterTypes?.materials || [],
      era: (filterQuery.data?.getAllFilterTypes?.eras || []).map((era) =>
        era.id.toLocaleLowerCase()
      ),
    }),
    [filterQuery.data?.getAllFilterTypes]
  );
  const tabsHelp = intl.formatMessage({
    id: 'sync.tabsHelp',
    defaultMessage:
      'Enter a tab name and press Enter. Leave empty to use all tabs.',
  });
  const statLabels = useMemo(() => {
    if (!sync?.stats) {
      return [];
    }

    const labels = [
      intl.formatMessage(
        { id: 'sync.stats.acolyte', defaultMessage: 'Acolyte: {count}' },
        { count: sync.stats.acolyte }
      ),
      intl.formatMessage(
        { id: 'sync.stats.sheet', defaultMessage: 'Sheet: {count}' },
        { count: sync.stats.sheet }
      ),
      intl.formatMessage(
        { id: 'sync.stats.amcr', defaultMessage: 'AMCR: {count}' },
        { count: sync.stats.amcr }
      ),
      intl.formatMessage(
        { id: 'sync.stats.changes', defaultMessage: 'Changes: {count}' },
        { count: changeCount }
      ),
      intl.formatMessage(
        { id: 'sync.stats.upToDate', defaultMessage: 'Up to date: {count}' },
        { count: sync.stats.upToDate }
      ),
      intl.formatMessage(
        { id: 'sync.stats.conflicts', defaultMessage: 'Conflicts: {count}' },
        { count: sync.stats.conflicts }
      ),
    ];

    if (sync.stats.unavailableAmcr) {
      labels.push(
        intl.formatMessage(
          {
            id: 'sync.stats.unavailableAmcr',
            defaultMessage: 'Unavailable AMCR: {count}',
          },
          { count: sync.stats.unavailableAmcr }
        )
      );
    }

    return labels;
  }, [changeCount, intl, sync?.stats]);

  useEffect(() => {
    if (!configQuery.loading) {
      setSheetId(config?.id || '');
      setConfiguredTabs(config?.tabs || []);
    }
  }, [config?.id, config?.tabs, configQuery.loading]);

  useEffect(() => {
    const nextDrafts: Record<string, SyncFields> = {};
    const nextSelection: MRT_RowSelectionState = {};

    rows.forEach((row) => {
      nextDrafts[row.key] = row.final;
      if (!row.conflict) {
        nextSelection[row.key] = true;
      }
    });

    setDrafts(nextDrafts);
    setRowSelection(nextSelection);
  }, [rows]);

  useEffect(() => {
    setTargetTab((current) =>
      current && tabs.includes(current) ? current : tabs[0] || ''
    );
  }, [tabs]);

  const handleSheetIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSheetId(event.target.value);
    },
    []
  );
  const handleTabsChange = useCallback(
    (_event: React.SyntheticEvent, values: string[]) => {
      setConfiguredTabs(
        Array.from(new Set(values.map((tab) => tab.trim()).filter(Boolean)))
      );
    },
    []
  );
  const handleTargetChange = useCallback((event) => {
    setTargetTab(String(event.target.value));
  }, []);
  const handleFieldChange = useCallback(
    (rowKey: string, field: SyncFieldKey, value: string) => {
      setDrafts((current) => ({
        ...current,
        [rowKey]: { ...current[rowKey], [field]: value || null },
      }));
    },
    []
  );
  const handleSaveConfig = useCallback(async () => {
    if (!siteId) {
      return;
    }

    setNotice(null);
    const result = await saveSite({
      variables: {
        id: siteId,
        site: sheetId.trim()
          ? { googleSheet: { id: sheetId.trim(), tabs: configuredTabs } }
          : { googleSheet: null },
      },
    });
    const errors = result.data?.updateSite?.errors;
    if (errors?.length) {
      setNotice({
        severity: 'error',
        text: errors.map((error) => error.error).join(' '),
      });
      return;
    }

    await configQuery.refetch();
    setNotice({
      severity: 'success',
      text: intl.formatMessage({
        id: 'sync.configSaved',
        defaultMessage: 'Google Sheet settings saved.',
      }),
    });
  }, [configQuery, configuredTabs, intl, saveSite, sheetId, siteId]);
  const handleLoad = useCallback(async () => {
    if (!siteId) {
      return;
    }

    setNotice(null);
    await loadPreview({ variables: { siteId, preview: true } });
  }, [loadPreview, siteId]);
  const handleCommit = useCallback(async () => {
    if (!siteId) {
      return;
    }

    const selectedRows = rows.filter((row) => rowSelection[row.key]);
    const result = await commit({
      variables: {
        siteId,
        input: {
          targetTab: targetTab || null,
          rows: selectedRows.map((row) => {
            const fields = drafts[row.key] || row.final;

            return {
              key: row.key,
              updatedAt: row.updatedAt,
              sheetFingerprint: row.sheetFingerprint,
              evId: fields.evId,
              amcrId: fields.amcrId,
              type: fields.type,
              material: fields.material,
              era: fields.era,
              description: fields.description,
              note: fields.note,
              foundAt: fields.foundAt,
            };
          }),
        },
      },
    });
    const counts = result.data?.commitFindSync;
    const failures = counts?.failures || [];
    setNotice({
      severity: failures.length ? 'error' : 'success',
      text: failures.length
        ? failures.map((failure) => failure.message).join(' ')
        : intl.formatMessage(
            {
              id: 'sync.commitSuccess',
              defaultMessage:
                'Synchronized {database} database changes and {sheet} sheet changes.',
            },
            {
              database:
                (counts?.createdAcolyte || 0) + (counts?.updatedAcolyte || 0),
              sheet: (counts?.createdSheet || 0) + (counts?.updatedSheet || 0),
            }
          ),
    });
    await loadPreview({ variables: { siteId, preview: true } });
  }, [
    commit,
    drafts,
    intl,
    loadPreview,
    rowSelection,
    rows,
    siteId,
    targetTab,
  ]);
  const selectedCount = Object.values(rowSelection).filter(Boolean).length;
  const needsTarget = Boolean(
    config && rows.some((row) => rowSelection[row.key] && !row.sheetTab)
  );
  const error =
    configQuery.error ||
    previewQuery.error ||
    saveState.error ||
    commitState.error;

  return (
    <Stack sx={pageStyles}>
      <Paper elevation={0} sx={cardStyles}>
        <Typography variant="h6" mb={2}>
          <FormattedMessage
            id="sync.settings"
            defaultMessage="Google Sheet settings"
          />
        </Typography>
        <Box sx={configGridStyles}>
          <FormField>
            <FieldLabel htmlFor="google-sheet-id">
              <FormattedMessage
                id="sync.sheetId"
                defaultMessage="Spreadsheet ID"
              />
            </FieldLabel>
            <SurfaceTextField
              id="google-sheet-id"
              onChange={handleSheetIdChange}
              value={sheetId}
            />
          </FormField>
          <FormField>
            <Stack direction="row" sx={labelRowStyles}>
              <FieldLabel htmlFor="google-sheet-tabs">
                <FormattedMessage id="sync.tabs" defaultMessage="Tabs" />
              </FieldLabel>
              <Tooltip arrow placement="top" title={tabsHelp}>
                <IconButton
                  aria-label={tabsHelp}
                  size="small"
                  sx={infoButtonStyles}
                >
                  <InfoOutlinedIcon sx={infoIconStyles} />
                </IconButton>
              </Tooltip>
            </Stack>
            <SurfaceAutocomplete<string, true, false, true>
              freeSolo
              multiple
              id="google-sheet-tabs"
              onChange={handleTabsChange}
              options={emptyTabs}
              value={configuredTabs}
            />
          </FormField>
        </Box>
        <Stack direction="row" gap={1} mt={2} sx={actionsStyles}>
          <Button
            disabled={saveState.loading}
            onClick={handleSaveConfig}
            tone="neutral"
          >
            <FormattedMessage
              id="sync.saveSettings"
              defaultMessage="Save settings"
            />
          </Button>
          <Button
            disabled={previewQuery.loading}
            onClick={handleLoad}
            startIcon={<SyncIcon />}
          >
            <FormattedMessage id="sync.load" defaultMessage="Load sync" />
          </Button>
          {sheetUrl && (
            <Link
              href={sheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
            >
              <Button endIcon={<LaunchIcon />} tone="neutral">
                <FormattedMessage
                  id="sync.openSheet"
                  defaultMessage="Open sheet"
                />
              </Button>
            </Link>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error.message}</Alert>}
      {notice && <Alert severity={notice.severity}>{notice.text}</Alert>}

      {(previewQuery.called || previewQuery.loading) && (
        <Stack gap={1}>
          <Stack direction="row" gap={2} sx={tableHeaderStyles}>
            <Stack sx={reviewHeadingStyles}>
              <Typography variant="h6">
                <FormattedMessage
                  id="sync.review"
                  defaultMessage="Review changes"
                />
              </Typography>
              {sync?.stats && (
                <Stack direction="row" gap={0.75} sx={statsStyles}>
                  <Typography color="text.secondary" variant="body2">
                    <FormattedMessage
                      id="sync.loaded"
                      defaultMessage="Loaded:"
                    />
                  </Typography>
                  {statLabels.map((label) => (
                    <Chip key={label} label={label} size="small" />
                  ))}
                </Stack>
              )}
              {tabs.length > 0 && (
                <Typography color="text.secondary" variant="body2">
                  <FormattedMessage id="sync.tabs" defaultMessage="Tabs" />:
                  {` ${tabs.join(', ')}`}
                </Typography>
              )}
            </Stack>
            {needsTarget && tabs.length > 1 && (
              <FormField sx={targetStyles}>
                <FieldLabel>
                  <FormattedMessage
                    id="sync.targetTab"
                    defaultMessage="Create missing rows in"
                  />
                </FieldLabel>
                <SurfaceSelect value={targetTab} onChange={handleTargetChange}>
                  {tabs.map((tab) => (
                    <MenuItem key={tab} value={tab}>
                      {tab}
                    </MenuItem>
                  ))}
                </SurfaceSelect>
              </FormField>
            )}
          </Stack>
          <SyncTable
            choices={choices}
            drafts={drafts}
            loading={previewQuery.loading}
            rows={rows}
            rowSelection={rowSelection}
            onFieldChange={handleFieldChange}
            onRowSelectionChange={setRowSelection}
          />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              disabled={
                commitState.loading ||
                selectedCount === 0 ||
                (needsTarget && tabs.length > 1 && !targetTab)
              }
              onClick={handleCommit}
            >
              <FormattedMessage
                id="sync.submit"
                defaultMessage="Synchronize selected"
              />
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
});
